/**
 * HOOK: maps a list through `build`, reusing an item's previous result while the item, `build` and that
 * item's own `inputsOf` values are unchanged. One row's update then rebuilds one row, not the list, and a
 * reused result is the same object, so React skips re-rendering it. `keyOf`, `inputsOf` and `build` must be stable.
 */
import { useMemo, useRef } from 'react';

interface ICachedItem<R> {
  inputs: readonly unknown[];
  result: R;
}

const sameInputs = (a: readonly unknown[], b: readonly unknown[]) =>
  a.length === b.length && a.every((value, index) => Object.is(value, b[index]));

export const useMemoPerItem = <T, I extends readonly unknown[], R>(
  items: readonly T[],
  keyOf: (item: T) => string,
  inputsOf: (item: T) => I,
  build: (item: T, inputs: I) => R,
): R[] => {
  const cache = useRef(new Map<string, ICachedItem<R>>());

  return useMemo(() => {
    const next = new Map<string, ICachedItem<R>>();
    const results = items.map(item => {
      const key = keyOf(item);
      const itemInputs = inputsOf(item);
      const inputs = [item, build, ...itemInputs];
      const cached = cache.current.get(key);
      const result = cached && sameInputs(cached.inputs, inputs) ? cached.result : build(item, itemInputs);
      next.set(key, { inputs, result });
      return result;
    });
    cache.current = next;

    return results;
  }, [items, keyOf, inputsOf, build]);
};
